import React, {useCallback, useEffect, useMemo} from 'react';
import {useShallow} from 'zustand/shallow';
import WizardLayout from 'src/components/wizard-layout/WizardLayout';
import {v4 as uuid} from 'uuid';
import {Step, STEPS} from './onboardingSteps';
import useRenderStepContent from './useRenderStepContent';
import useStepData from './useStepData';
import useStepMutation from './useStepMutation';
import {useOnboardingStore} from './OnboardingStore';
import useStepValidation, {isStepValid} from './useStepValidation';
import {portfolioStepIncompleteMessage} from './portfolioStepIncompleteMessage';
import {hasProfilePicture} from './isAnonymousAvatarUrl';
import useStepOrder from './useStepOrder';
import {useOnboardingAnalytics} from './useOnboardingAnalytics';
import {useUserData} from '@/hooks/useUserData';
import useScreenPresentationDetails from './useScreenPresentationDetails';
import authenticateInstagram from '@/services/instagram/Instagram';
import {navigateWithAnalytics} from '@/utils/wrappers/Delta';
import {Platform} from '@/types/platforms';
import useAnalyticsScreenOptions from './useAnalyticsScreenOptions';
import useStepBackgroundColor from './useStepBackgroundColor';
import usePrefetchAssets from '@/hooks/use-prefetch-assets/usePrefetchAssets';
import {
  COMPLETION_MOBILE_VIDEO_URL,
  COMPLETION_VIDEO_URL,
} from './steps/OnboardingCompletion';
import {Asset} from '@/hooks/use-prefetch-assets';
import PoppaysLoader from '@/components/poppays-loader/PoppaysLoader';
import ContactUsButton from '@/components/contact-us-button/ContactUsButton';

const ONBOARDING_ASSETS: Asset[] = [
  ...Object.values(STEPS)
    .filter((step) => step !== STEPS.COMPLETION)
    .map((step) => {
      let imageName = step;
      if (step === STEPS.INSTAGRAM_INSTRUCTIONS)
        imageName = STEPS.SYNC_PLATFORMS;

      return {
        url: `/assets/images/onboarding/${imageName}.jpg`,
        type: 'image' as const,
      };
    }),
  {
    url: COMPLETION_MOBILE_VIDEO_URL,
    type: 'video' as const,
  },
  {
    url: COMPLETION_VIDEO_URL,
    type: 'video' as const,
  },
];

function OnboardingView() {
  useStepValidation();
  useStepOrder();

  usePrefetchAssets(ONBOARDING_ASSETS);

  const [
    step,
    isCurrentStepValid,
    banner,
    previousStep,
    stepOrder,
    isStepUploading,
    isStepOrderPersisted,
  ] = useOnboardingStore(
    useShallow((state) => [
      state.step,
      state.isCurrentStepValid,
      state.banner,
      state.previousStep,
      state.stepOrder,
      state.isStepUploading,
      state.isStepOrderPersisted,
    ])
  );

  const [
    setStep,
    closeBanner,
    clearPersistentData,
    setAnalyticsFlowId,
    analyticsFlowId,
    flowName,
  ] = useOnboardingStore(
    useShallow((state) => [
      state.setStep,
      state.closeBanner,
      state.clearPersistentData,
      state.setAnalyticsFlowId,
      state.analyticsFlowId,
      state.flowName,
    ])
  );

  const flowId = analyticsFlowId || uuid();
  const {
    sendSignUpFlowStartedEvent,
    sendSignUpScreenPresentedEvent,
    sendSignUpScreenDismissedEvent,
    useSendNewConnectedPlatformEvent,
    sendSignUpFlowEndedEvent,
    sendSignUpScreenButtonPressedEvent,
    clearStoredProcessIds,
  } = useOnboardingAnalytics(flowId, flowName ?? '');
  const {connectedPlatforms, user} = useUserData({});
  useSendNewConnectedPlatformEvent(connectedPlatforms);

  useEffect(() => {
    if (analyticsFlowId && flowName) {
      sendSignUpFlowStartedEvent();
    }
  }, [analyticsFlowId, sendSignUpFlowStartedEvent, flowName]);

  if (!analyticsFlowId) {
    setAnalyticsFlowId(flowId);
  }

  useEffect(() => {
    const stepIndex = stepOrder.indexOf(step);
    if (analyticsFlowId && stepIndex === 0 && flowName) {
      const presentationDetails = getScreenPresentationDetails(step);
      const presentedScreenOptions = analyticsScreenOptions(step);
      sendSignUpScreenPresentedEvent({
        step,
        source: 'initial-step',
        details: presentationDetails,
        options: presentedScreenOptions,
        dismissedReason: 'initial-step',
      });
    }
    // I intentionally don't add step as a dependency because I want to send the event only once
    // after we have the analyticsFlowId
  }, [analyticsFlowId, sendSignUpScreenPresentedEvent, flowName]);

  useStepFromUrl(setStep, stepOrder);
  useHandleErrorFromUrl();

  // Wait for step order to be initialized before rendering
  // This prevents showing the wrong step (e.g., first step) before useStepOrder
  // has determined the correct step (e.g., REONBOARDING step)
  const isStepOrderInitialized = flowName !== undefined || isStepOrderPersisted;

  const stepIndex = React.useMemo(() => {
    return stepOrder.indexOf(step);
  }, [step, stepOrder]);

  const stepDataProps = useStepData();
  const stepMutation = useStepMutation();

  const progress = (stepIndex + 1) / stepOrder.length;
  useEffect(() => {
    if (progress >= 1) {
      sendSignUpFlowEndedEvent('success', connectedPlatforms);
    }
  }, [progress, sendSignUpFlowEndedEvent, connectedPlatforms]);

  const getScreenPresentationDetails = useScreenPresentationDetails();
  const analyticsScreenOptions = useAnalyticsScreenOptions();

  const sendStepTransitionAnalytics = useCallback(
    (currentStep: Step, nextStep?: Step) => {
      const dismissalDetails = getScreenPresentationDetails(currentStep);
      const dismissedScreenOptions = analyticsScreenOptions(currentStep);
      const reason = `moved from ${currentStep} to ${nextStep}`;
      const source = previousStep ?? 'unknown';
      sendSignUpScreenDismissedEvent({
        step: currentStep,
        source,
        details: dismissalDetails,
        options: dismissedScreenOptions,
        dismissedReason: reason,
      });
      if (nextStep) {
        const presentationDetails = getScreenPresentationDetails(nextStep);
        const presentedScreenOptions = analyticsScreenOptions(nextStep);
        sendSignUpScreenPresentedEvent({
          step: nextStep,
          source: currentStep,
          details: presentationDetails,
          options: presentedScreenOptions,
          dismissedReason: reason,
        });
      }
    },
    [
      getScreenPresentationDetails,
      sendSignUpScreenDismissedEvent,
      sendSignUpScreenPresentedEvent,
      analyticsScreenOptions,
      previousStep,
    ]
  );

  const onBack = useMemo(() => {
    if (step === STEPS.COMPLETION) {
      return undefined;
    }
    if (previousStep && stepIndex !== 0) {
      return () => {
        // fallback if the step isn't in stepOrder (index -1),
        // such as when the ?step= query param is used
        const targetStep =
          stepIndex > 0 ? stepOrder[stepIndex - 1] : previousStep;
        setStep(targetStep);
        sendSignUpScreenButtonPressedEvent('Back', step);
        sendStepTransitionAnalytics(step, targetStep);
      };
    }
    return undefined;
  }, [
    previousStep,
    setStep,
    sendStepTransitionAnalytics,
    step,
    stepIndex,
    stepOrder,
    sendSignUpScreenButtonPressedEvent,
  ]);

  const onNext = useCallback(async () => {
    const state = useOnboardingStore.getState();
    if (
      step === STEPS.PORTFOLIO &&
      !isStepValid(step, state, connectedPlatforms)
    ) {
      state.openBanner({
        title: portfolioStepIncompleteMessage({
          hasProfilePicture: hasProfilePicture(state.profilePictureUrl),
          hasCoverPhoto: state.portfolioMediaItems.length > 0,
        }),
        children: undefined,
        severity: 'error',
      });
      return;
    }

    if (step === STEPS.COMPLETION) {
      clearPersistentData();
      clearStoredProcessIds();
      await navigateWithAnalytics('/');
      return;
    }
    if (step === STEPS.INSTAGRAM_INSTRUCTIONS) {
      sendSignUpScreenButtonPressedEvent('Connect Instagram', step);
      const redirectQueryParams = {
        step: STEPS.SYNC_PLATFORMS,
      };
      authenticateInstagram(user?.id ?? null, redirectQueryParams);
      return;
    }
    await stepMutation();
    if (stepIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[stepIndex + 1];
      sendSignUpScreenButtonPressedEvent('Next', step);
      sendStepTransitionAnalytics(step, nextStep);
      setStep(nextStep);
    }
  }, [
    step,
    stepMutation,
    stepIndex,
    stepOrder,
    sendSignUpScreenButtonPressedEvent,
    user?.id,
    sendStepTransitionAnalytics,
    setStep,
    connectedPlatforms,
  ]);

  const stepContent = useRenderStepContent(onNext);
  const desktopVisualBackgroundColor = useStepBackgroundColor();

  const renderDesktopVisual = () => {
    let imageName = step;
    if (step === STEPS.INSTAGRAM_INSTRUCTIONS) imageName = STEPS.SYNC_PLATFORMS;
    if (step === STEPS.COMPLETION) {
      return (
        <video
          src={COMPLETION_VIDEO_URL}
          preload="auto"
          playsInline
          muted
          autoPlay
        />
      );
    }

    return (
      <img
        src={`/assets/images/onboarding/${imageName}.jpg`}
        alt="Step decoration"
      />
    );
  };

  const onSkip = undefined;
  // const onSkip = async () => {
  //   await navigateWithAnalytics('/');
  // };

  // Render contact us button in top nav (opposite to back button) for reonboarding flow (except on completion step
  // where we don't show the progress bar)
  const renderTopNavRight =
    flowName === 'reonboarding' && step !== STEPS.COMPLETION
      ? () => <ContactUsButton screen_name={step} />
      : undefined;

  // Don't render until step order is initialized to prevent showing wrong step
  if (!isStepOrderInitialized) {
    return <PoppaysLoader />;
  }

  return (
    <WizardLayout
      banner={banner}
      onCloseBanner={closeBanner}
      progress={progress}
      renderDesktopVisual={renderDesktopVisual}
      desktopVisualBackgroundColor={desktopVisualBackgroundColor}
      renderContent={() => stepContent}
      renderTopNavRight={renderTopNavRight}
      onBack={onBack}
      onNext={onNext}
      onSkip={onSkip}
      isNextDisabled={step === STEPS.PORTFOLIO ? false : !isCurrentStepValid}
      isUploading={isStepUploading}
      {...stepDataProps}
    />
  );
}

function isValidStep(step: string | null): step is Step {
  return Boolean(step && Object.values(STEPS).includes(step as Step));
}

function useStepFromUrl(setStep: (step: Step) => void, stepOrder: Step[]) {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get('step');
    if (isValidStep(stepParam)) {
      setStep(stepParam);
    }
  }, [setStep, stepOrder]);
}

function getPlatformFromString(provider?: string): Platform | null {
  if (!provider) {
    return null;
  }
  if (provider.toLowerCase().includes('instagram')) {
    return Platform.Instagram;
  }
  if (provider.toLowerCase().includes('tiktok')) {
    return Platform.TikTok;
  }
  if (provider.toLowerCase().includes('youtube')) {
    return Platform.YouTube;
  }
  if (provider.toLowerCase().includes('facebook')) {
    return Platform.Facebook;
  }
  return null;
}

function useHandleErrorFromUrl() {
  const [setPlatformError, analyticsFlowId, flowName] = useOnboardingStore(
    useShallow((state) => [
      state.setPlatformError,
      state.analyticsFlowId,
      state.flowName,
    ])
  );

  const {sendSignUpStepEndedEvent} = useOnboardingAnalytics(
    analyticsFlowId ?? '',
    flowName ?? ''
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const message = urlParams.get('message');
    const providerInput = urlParams.get('provider');
    const provider = getPlatformFromString(providerInput ?? '');
    const success = urlParams.get('success') === 'true';
    if (!success) {
      if (provider) {
        sendSignUpStepEndedEvent(provider, 'error', message ?? '');
      }
      setPlatformError(message ?? undefined);
    }
  }, [setPlatformError, sendSignUpStepEndedEvent, analyticsFlowId]);
}

export default OnboardingView;
